import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'

import { getExcelPauseEligibility } from 'api'
import ModalForm from 'app/components/ModalForm'
import constants from 'app/constants'
import { confirmDialog, eventAlert, eventEmitter } from 'app/helpers'

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginBottom: theme.spacing(2),
    width: '100%'
  }
}))

// Excel program-enrolment pause: ops selects a single month to pause (the
// current month or a future one, but never two consecutive months). The chosen
// month is sent to the backend, which creates a journey PAUSE_REQUEST. Current
// month -> starts today and refunds the prorata coins (operator confirms the
// refund first); a future month -> starts on the 1st. Month options + the
// refund figure come from the journey pause eligibility.
const PauseForm = ({ isModalOpen, formData, onSaveClick, closeModal }) => {
  const classes = useStyles()
  const [months, setMonths] = useState([])
  const [values, setValues] = useState({ month_key: '' })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const hasFetchedRef = useRef(false)

  const monthKey = (m) => `${m.year}-${m.month}`
  const selectedMonth = months.find((m) => monthKey(m) === values.month_key)
  // Current-month pause runs from today and refunds the prorata coins for the
  // rest of the month; only the current month carries a coin_refund_calculation.
  const refundCoins =
    selectedMonth?.coin_refund_calculation?.total_refund_amount

  const setField = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }))
    setErrors((prev) => ({ ...prev, [field]: null }))
  }

  const handleSave = async () => {
    const newErrors = {}
    if (!selectedMonth) {
      newErrors.month_key = 'Please select a month'
    } else if (!selectedMonth.pause_eligible) {
      newErrors.month_key =
        selectedMonth.reason || 'This month cannot be paused'
    }
    if (Object.keys(newErrors).length) {
      setErrors(newErrors)
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
      return
    }
    // Pausing the current month starts today and refunds coins — confirm the
    // refund amount with the operator before submitting.
    if (selectedMonth.is_current_month) {
      const coins = refundCoins || 0
      const { isConfirmed } = await confirmDialog({
        text: `Pausing ${
          selectedMonth.label
        } will start today. ${coins} coin${
          coins === 1 ? '' : 's'
        } will be refunded to the parent's wallet. Continue?`,
        confirmButtonText: 'Yes, pause & refund',
        options: { title: 'Confirm pause refund' }
      })
      if (!isConfirmed) {
        eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
        return
      }
    }
    onSaveClick(
      { month: selectedMonth.month, year: selectedMonth.year },
      'pause_enrolment'
    )
  }

  useEffect(() => {
    if (!isModalOpen || !formData?.uuid || hasFetchedRef.current) return
    hasFetchedRef.current = true

    const checkEligibility = async () => {
      try {
        setIsLoading(true)
        const response = await getExcelPauseEligibility(formData.uuid)
        const data = response?.data
        if (!data?.pause_eligible) {
          eventAlert(
            data?.reason || 'This enrolment is not eligible for pausing.',
            constants.ERROR
          )
          closeModal()
          return
        }
        if (!data.eligible_months || data.eligible_months.length === 0) {
          eventAlert(
            'No upcoming months are available to pause for this enrolment.',
            constants.ERROR
          )
          closeModal()
          return
        }
        setMonths(data.eligible_months)
      } catch (err) {
        eventAlert(
          'Unable to check pause eligibility. Please try again.',
          constants.ERROR
        )
        closeModal()
      } finally {
        setIsLoading(false)
      }
    }

    checkEligibility()
  }, [isModalOpen, formData?.uuid, closeModal])

  useEffect(() => {
    if (!isModalOpen) hasFetchedRef.current = false
  }, [isModalOpen])

  return (
    <ModalForm
      isModalOpen={isModalOpen}
      onModalClose={closeModal}
      onSaveClick={handleSave}
      modalTitle="Pause enrolment"
      buttonLabel="Confirm"
      maxWidth="sm"
      loading={isLoading ? true : undefined}
    >
      <FormControl
        className={classes.formControl}
        variant="outlined"
        error={!!errors.month_key}
        required
      >
        <InputLabel>Pause month</InputLabel>
        <Select
          value={values.month_key}
          onChange={(e) => setField('month_key', e.target.value)}
          label="Pause month"
          disabled={isLoading}
        >
          {months.map((m) => (
            <MenuItem
              key={monthKey(m)}
              value={monthKey(m)}
              disabled={!m.pause_eligible}
            >
              {m.pause_eligible ? m.label : `${m.label} (${m.reason})`}
            </MenuItem>
          ))}
        </Select>
        {errors.month_key && (
          <FormHelperText>{errors.month_key}</FormHelperText>
        )}
        {selectedMonth?.is_current_month && (
          <FormHelperText>
            Pauses from today. {refundCoins || 0} coin
            {(refundCoins || 0) === 1 ? '' : 's'} will be refunded to the
            parent&apos;s wallet.
          </FormHelperText>
        )}
      </FormControl>
    </ModalForm>
  )
}

PauseForm.propTypes = {
  isModalOpen: PropTypes.bool,
  formData: PropTypes.object,
  onSaveClick: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired
}

export default PauseForm
